import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import { v4 as uuidv4 } from "uuid";
import ContentBlockSuggestionList from "../components/ContentBlockSuggestionList";

// 回退的 DOMRect，当无法获取真实位置时使用
const DOM_RECT_FALLBACK = {
    x: 0,
    y: 0,
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: 0,
    height: 0,
    toJSON: () => ({}),
};

/**
 * 检查 clientRect 是否有效
 * @param {DOMRect} clientRect - 要检查的矩形对象
 * @returns {boolean} 是否无效
 */
function isInvalidRect(clientRect) {
    return !clientRect || (clientRect.width === 0 && clientRect.height === 0) || (clientRect.top === 0 && clientRect.left === 0 && clientRect.bottom === 0 && clientRect.right === 0);
}

/**
 * 手动计算光标位置的 clientRect
 * @param {Editor} editor - Tiptap 编辑器实例
 * @returns {DOMRect|null} 计算得到的矩形对象，失败时返回 null
 */
function calculateCursorRect(editor) {
    try {
        const { view } = editor;
        const { from } = view.state.selection;

        // 使用 ProseMirror 的 coordsAtPos 方法获取坐标
        const coords = view.coordsAtPos(from);

        if (coords && coords.top > 0 && coords.left > 0) {
            return {
                x: coords.left,
                y: coords.top,
                top: coords.top,
                left: coords.left,
                bottom: coords.bottom || coords.top + 20,
                right: coords.right || coords.left + 1,
                width: (coords.right || coords.left + 1) - coords.left,
                height: (coords.bottom || coords.top + 20) - coords.top,
                toJSON: () => ({}),
            };
        }
    } catch (error) {
        console.error("计算光标位置失败:", error);
    }

    return null;
}

/**
 * 获取有效的 clientRect，优先使用 Tiptap 提供的，失败时手动计算
 * @param {Object} props - suggestion 的 props
 * @returns {DOMRect|null} 有效的矩形对象，失败时返回 null
 */
function getValidClientRect(props) {
    let clientRect = props.clientRect?.() || DOM_RECT_FALLBACK;

    if (isInvalidRect(clientRect)) {
        // 原始坐标无效，尝试手动计算
        clientRect = calculateCursorRect(props.editor);
    }

    return clientRect;
}

/**
 * 创建 tippy 弹窗实例
 * @param {DOMRect} clientRect - 弹窗定位的矩形
 * @param {HTMLElement} content - 弹窗内容元素
 * @returns {Array} tippy 实例数组
 */
function createTippyInstance(clientRect, content) {
    return tippy("body", {
        getReferenceClientRect: () => clientRect,
        appendTo: () => document.body,
        content: content,
        showOnCreate: true,
        interactive: true,
        trigger: "manual",
        placement: "bottom-start",
        theme: "light",
        maxWidth: 300,
        zIndex: 9999,
    });
}

/**
 * 判断内容块类型是否适合作为行内元素
 * @param {string} contentBlockType - 内容块类型
 * @returns {boolean} 是否适合行内显示
 */
function shouldBeInline(contentBlockType) {
    // 这些类型适合行内显示（简单、短小的内容）
    const inlineTypes = [
        "scheduleInfo", // 计划基本信息（通常是简短文本）
        "contact", // 单个联系人
        "attachment", // 单个附件
        "datetime", // 日期时间
        "signature", // 签名
        "flightInfo", // 单个航班信息
        "airportInfo", // 机场信息
        "fuelInfo", // 燃油信息
    ];

    return inlineTypes.includes(contentBlockType);
}

/**
 * 内容块建议配置
 * 用于 @ 提及功能，提供内容块的建议和插入功能
 */
export const createContentBlockSuggestion = (availableBlocks = []) => {
    return {
        char: "@",
        allowedPrefixes: null,
        items: ({ query }) => {
            return availableBlocks
                .filter((block) => {
                    if (!block.data) return false;
                    const searchText = query.toLowerCase();
                    const title = (block.title || "").toLowerCase();
                    const type = (block.type || "").toLowerCase();
                    return title.includes(searchText) || type.includes(searchText);
                })
                .map((block) => ({
                    ...block,
                    contentBlockType: block.type,
                    contentBlockLabel: block.title,
                    description: getBlockDescription(block),
                    icon: getBlockIcon(block.type),
                }))
                .slice(0, 10);
        },
        render: () => {
            let component;
            let popup;

            return {
                onStart: (props) => {
                    component = new ReactRenderer(ContentBlockSuggestionList, {
                        props,
                        editor: props.editor,
                    });

                    const clientRect = getValidClientRect(props);
                    if (!clientRect) {
                        return; // 无法获取有效坐标，跳过弹窗显示
                    }

                    popup = createTippyInstance(clientRect, component.element);
                },

                onUpdate(props) {
                    component.updateProps(props);

                    const clientRect = getValidClientRect(props);
                    if (!clientRect) {
                        return; // 无法获取有效坐标，跳过弹窗更新
                    }

                    if (!popup || popup.length === 0) {
                        // 弹窗不存在，重新创建
                        popup = createTippyInstance(clientRect, component.element);
                    } else {
                        // 更新现有弹窗的位置
                        popup[0].setProps({
                            getReferenceClientRect: () => clientRect,
                        });
                    }
                },

                onKeyDown(props) {
                    if (props.event.key === "Escape") {
                        popup?.[0]?.hide();
                        return true;
                    }
                    return component?.ref?.onKeyDown(props);
                },

                onExit() {
                    if (popup && popup.length > 0) {
                        popup[0].destroy();
                    }
                    if (component) {
                        component.destroy();
                    }
                },
            };
        },
        command: ({ editor, range, props }) => {
            const { id, contentBlockType } = props;

            // 根据内容块类型决定使用行内块还是块级块
            const isInline = shouldBeInline(contentBlockType);
            const nodeType = isInline ? "inlineContentBlock" : "contentBlock";

            // V2.5: 生成唯一的instanceId
            const instanceId = uuidv4();

            const node = {
                type: nodeType,
                attrs: {
                    contentBlockId: id,
                    contentBlockType: contentBlockType,
                    contentBlockLabel: props.contentBlockLabel,
                    dataKey: id,
                    status: "filled",
                    isInline: isInline,
                    instanceId: instanceId, // V2.5: 添加instanceId
                },
            };

            if (isInline) {
                // 行内插入：直接在当前位置插入
                editor.chain().focus().deleteRange(range).insertContent(node).run();
            } else {
                // 块级插入：在新行插入，确保前后有换行
                editor
                    .chain()
                    .focus()
                    .deleteRange(range)
                    .insertContent([
                        { type: "paragraph", content: [] }, // 确保前面有段落
                        node,
                        { type: "paragraph", content: [] }, // 确保后面有段落
                    ])
                    .run();
            }
        },
    };
};

/**
 * 获取内容块的描述文本
 * @param {Object} block - 内容块对象
 * @returns {string} 描述文本
 */
function getBlockDescription(block) {
    const { type, data } = block;
    const isInline = shouldBeInline(type);

    switch (type) {
        case "scheduleInfo":
            return isInline ? "行内：计划基本信息" : "块级：计划基本信息";
        case "flightList":
        case "scheduleFlightList":
            const count = Array.isArray(data) ? data.length : 0;
            return isInline ? `行内：${count} 个航班` : `块级：${count} 个航班列表`;
        case "flightInfo":
            return isInline ? "行内：航班信息" : "块级：航班信息";
        case "contact":
            return isInline ? "行内：联系人" : "块级：联系人";
        case "contactList":
            const contactCount = Array.isArray(data) ? data.length : 0;
            return isInline ? `行内：${contactCount} 个联系人` : `块级：${contactCount} 个联系人列表`;
        case "attachment":
            return isInline ? "行内：附件" : "块级：附件";
        case "attachmentList":
            const attachmentCount = Array.isArray(data) ? data.length : 0;
            return isInline ? `行内：${attachmentCount} 个附件` : `块级：${attachmentCount} 个附件列表`;
        case "datetime":
            return isInline ? "行内：日期时间" : "块级：日期时间";
        case "signature":
            return isInline ? "行内：签名" : "块级：签名";
        case "airportInfo":
            return isInline ? "行内：机场信息" : "块级：机场信息";
        case "fuelInfo":
            return isInline ? "行内：燃油信息" : "块级：燃油信息";
        case "fuelRequest":
            return isInline ? "行内：燃油申请" : "块级：燃油申请";
        case "cargoManifest":
            return isInline ? "行内：货物清单" : "块级：货物清单";
        case "cargoList":
            return isInline ? "行内：货物列表" : "块级：货物列表";
        case "crewList":
            return isInline ? "行内：机组列表" : "块级：机组列表";
        default:
            return isInline ? "行内：内容块" : "块级：内容块";
    }
}

/**
 * 获取内容块的图标
 * @param {string} type - 内容块类型
 * @returns {string} 图标字符
 */
function getBlockIcon(type) {
    switch (type) {
        case "scheduleInfo":
            return "📅";
        case "flightList":
        case "scheduleFlightList":
        case "flightInfo":
            return "✈️";
        case "contact":
        case "contactList":
            return "👥";
        case "attachment":
        case "attachmentList":
            return "📎";
        case "datetime":
            return "🕐";
        case "signature":
            return "✍️";
        case "airportInfo":
            return "🏢";
        case "fuelInfo":
        case "fuelRequest":
            return "⛽";
        case "cargoManifest":
        case "cargoList":
            return "📦";
        case "crewList":
            return "👨‍✈️";
        default:
            return "📝";
    }
}
